import { type RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import type { ProgressAltRevealTarget } from './progressAltCursorLib';
import { STICKY_INSET_PX } from './progressAltMetrics';

/**
 * The scroll container, held two ways at once, because scrolling needs both and neither will do alone.
 *
 * Scrolling means writing to the node, and a node written to belongs in a ref — the compiler holds
 * state values immutable, rightly. But a ref never changes identity, so an effect keyed on one runs
 * once per mount of whatever owns it and never again. The grid replaces its scroll container without
 * unmounting, every time it swaps in an empty state and back, and effects keyed on the ref kept their
 * listeners bound to a node no longer on the page: the wheel stopped working while everything native
 * carried on. The token changes whenever the node does, which is what makes the listeners follow it.
 */
export type ProgressAltScrollTarget = {
  ref: RefObject<HTMLDivElement | null>;
  /** Changes whenever the node does. Its value is never read — only that it changed. */
  token: number;
  /** Hand this to `ref` on the scroll container. Stable, so React calls it only on a real swap. */
  attach: (node: HTMLDivElement | null) => void;
};

export const useScrollTarget = (): ProgressAltScrollTarget => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [token, setToken] = useState(0);
  const attach = useCallback((node: HTMLDivElement | null) => {
    ref.current = node;
    setToken(previous => previous + 1);
  }, []);
  return { ref, token, attach };
};

/**
 * Restores the horizontal scrolling that `overflow-x: hidden` takes away.
 *
 * The grid hides its horizontal overflow so the browser draws no bar for it — the custom one below
 * stands in — but `overflow-x: hidden` still leaves the element programmatically scrollable, so
 * sticky positioning and `scrollLeft` keep working. Only the user's own wheel and trackpad gestures
 * are blocked, and this hands them back. Suppressing the bar this way rather than through
 * `::-webkit-scrollbar` rules leaves the vertical scrollbar exactly as the browser draws it, which
 * `::-webkit-scrollbar` would not: styling either bar opts the element into custom scrollbars for
 * both.
 */
export const useHorizontalWheelScrolling = ({ ref, token }: ProgressAltScrollTarget) => {
  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;

    const onWheel = (event: WheelEvent) => {
      const delta = event.deltaX !== 0 ? event.deltaX : event.shiftKey ? event.deltaY : 0;
      if (delta === 0) return;
      const before = element.scrollLeft;
      element.scrollLeft = before + delta;
      // Only swallow the gesture when it actually moved something, so an at-the-edge swipe can
      // still fall through to whatever else wants it.
      if (element.scrollLeft !== before) event.preventDefault();
    };

    element.addEventListener('wheel', onWheel, { passive: false });
    return () => element.removeEventListener('wheel', onWheel);
  }, [ref, token]);
};

/**
 * Where the columns sit, as far as anchoring is concerned. Both halves move together — collapsing
 * the date column narrows the sticky inset and slides every column left by the same amount — so they
 * travel as one value rather than as two that could be read from different moments.
 */
export type ProgressAltScrollLayout = {
  /** Where each column's left edge falls on the canvas. */
  columnOffsetsPx: readonly number[];
  /** How much of every scroll position the sticky date column covers. */
  stickyInsetPx: number;
};

/**
 * Where every cell sits on the canvas and how big it is, and how much of the canvas the sticky header
 * and date column cover. Enough to put any one cell on screen.
 */
export type ProgressAltCellLayout = {
  columnOffsetsPx: readonly number[];
  columnWidthsPx: readonly number[];
  /** The top edge of each photo row, the header row already accounted for. */
  rowOffsetsPx: readonly number[];
  rowHeightsPx: readonly number[];
  stickyLeftInsetPx: number;
  stickyTopInsetPx: number;
  /** One tile's height, which is how much of a row it takes to read it. See `getRowRevealScroll`. */
  photoHeightPx: number;
};

/** Where the scrollport is looking and how much of it there is. */
export type ProgressAltRevealScrollport = {
  scrollLeft: number;
  scrollTop: number;
  clientWidth: number;
  clientHeight: number;
};

/** Where the grid is scrolled to. Outlives the node it was read from — see `useRememberedScrolling`. */
export type ProgressAltScrollPosition = {
  scrollLeft: number;
  scrollTop: number;
};

export type ProgressAltRevealScroll = ProgressAltScrollPosition & {
  /** Whether the movement is a nudge, and so worth animating. See `getAxisRevealScroll`. */
  smooth: boolean;
};

/**
 * Where to scroll to present a column: centred in the stretch of scrollport the sticky date column
 * leaves visible when it fits there, or its start laid against that stretch's near edge — with the
 * usual clear air off the date column — when it is wider. Absolute rather than minimal, unlike the
 * rows below: going to a column means being shown it, and it is always shown the same way wherever
 * the reader happened to be.
 */
const getColumnRevealScroll = ({ scrollPx, viewportPx, stickyInsetPx, startPx, sizePx }: {
  scrollPx: number;
  viewportPx: number;
  /** How much of the near end of the viewport the sticky date column covers. */
  stickyInsetPx: number;
  startPx: number;
  sizePx: number;
}): { scrollPx: number; jumped: boolean } => {
  const visiblePx = viewportPx - stickyInsetPx;
  const targetPx = sizePx > visiblePx
    ? startPx - stickyInsetPx - STICKY_INSET_PX
    : startPx - stickyInsetPx - (visiblePx - sizePx) / 2;
  // With none of the column on screen there is no continuity for an animation to carry.
  const onScreenPx = Math.min(startPx + sizePx, scrollPx + viewportPx) - Math.max(startPx, scrollPx + stickyInsetPx);
  return { scrollPx: Math.max(0, Math.round(targetPx)), jumped: onScreenPx <= 0 };
};

/**
 * What it takes to have enough of a row on screen, and how far the view has to move to get there.
 *
 * "Enough" is one photo, not the whole row — a row shorter than that, which is what a collapsed row
 * is, is enough of itself. It is counted inside a region held `STICKY_INSET_PX` clear of both ends of
 * the scrollport — the same margin a sticky header keeps — so a row brought into view is never flush
 * against the edge it came in over, and one already flush against it counts as still wanting a nudge.
 *
 * Three cases, and the reader's place is disturbed as little as each allows:
 *
 * - None of it in that region: lay its near edge against the region's near edge. The reader was looking
 *   somewhere else entirely, so there is no place to keep, and nothing to animate.
 * - Some of it, but less than a photo's worth: move by exactly the shortfall and no further. Worth
 *   animating — the reader can see where they already are, so they can follow the movement.
 * - A photo's worth or more: nothing at all. Going to a row the reader is already reading must not
 *   shift the grid under them.
 */
const getRowRevealScroll = ({ scrollPx, viewportPx, stickyInsetPx, startPx, sizePx, photoPx }: {
  scrollPx: number;
  viewportPx: number;
  /** How much of the near end of the viewport the sticky header covers. */
  stickyInsetPx: number;
  startPx: number;
  sizePx: number;
  /** One photo's height — as much of the row as it takes to read it. */
  photoPx: number;
}): { scrollPx: number; jumped: boolean } => {
  const comfortableStartPx = scrollPx + stickyInsetPx + STICKY_INSET_PX;
  const comfortableEndPx = scrollPx + viewportPx - STICKY_INSET_PX;
  const endPx = startPx + sizePx;
  const onScreenPx = Math.min(endPx, comfortableEndPx) - Math.max(startPx, comfortableStartPx);
  // A track shorter than a photo — a collapsed row — is enough of itself.
  const enoughPx = Math.min(photoPx, sizePx);

  if (onScreenPx >= enoughPx) return { scrollPx, jumped: false };
  if (onScreenPx <= 0) {
    return { scrollPx: Math.max(0, startPx - stickyInsetPx - STICKY_INSET_PX), jumped: true };
  }
  return {
    // Whichever edge it is hanging off, the shortfall is made up against that edge and no other.
    scrollPx: Math.max(
      0,
      startPx < comfortableStartPx
        ? endPx - enoughPx - stickyInsetPx - STICKY_INSET_PX
        : startPx + enoughPx + STICKY_INSET_PX - viewportPx,
    ),
    jumped: false,
  };
};

/**
 * Where to scroll to present a cell, and whether the movement is small enough to be worth animating.
 *
 * The two axes answer differently. Across, the column is presented outright — centred, or start-aligned
 * when it outsizes the view. Down, the reader's place in time is disturbed as little as possible, and a
 * target naming no row holds the vertical scroll exactly where it is — which is what keeps a change of
 * scope from also moving the reader through time.
 *
 * Animated only when nothing had to jump. A jump means the cell was somewhere the reader could not see,
 * so there is no continuity for an animation to carry; anything less means they can see where they
 * already are, so they can follow the movement.
 */
export const getRevealScroll = (
  { rowIndex, columnIndex }: ProgressAltRevealTarget,
  layout: ProgressAltCellLayout,
  scrollport: ProgressAltRevealScrollport,
): ProgressAltRevealScroll => {
  const horizontal = getColumnRevealScroll({
    scrollPx: scrollport.scrollLeft,
    viewportPx: scrollport.clientWidth,
    stickyInsetPx: layout.stickyLeftInsetPx,
    startPx: layout.columnOffsetsPx[columnIndex] ?? 0,
    sizePx: layout.columnWidthsPx[columnIndex] ?? 0,
  });
  const vertical = rowIndex == null
    ? { scrollPx: scrollport.scrollTop, jumped: false }
    : getRowRevealScroll({
      scrollPx: scrollport.scrollTop,
      viewportPx: scrollport.clientHeight,
      stickyInsetPx: layout.stickyTopInsetPx,
      startPx: layout.rowOffsetsPx[rowIndex] ?? 0,
      sizePx: layout.rowHeightsPx[rowIndex] ?? 0,
      photoPx: layout.photoHeightPx,
    });

  return {
    scrollLeft: horizontal.scrollPx,
    scrollTop: vertical.scrollPx,
    smooth: !horizontal.jumped && !vertical.jumped,
  };
};

/**
 * Carries the grid's scroll position across the trips it is unmounted for, and puts it back on arrival.
 *
 * The fullscreen page replaces the grid in the DOM rather than covering it, so the grid that comes back
 * is on a new node, scrolled to nothing however far the reader had got. Recorded on the way out and
 * restored on the way in, the position survives — which is what leaves `useRevealedCellScrolling`
 * something to measure its "already on screen" against. The empty states swap the node the same way,
 * and keep their place for the same reason.
 *
 * Keyed to the node alone. Restoring on anything else would undo the reader's own scrolling.
 */
export const useRememberedScrolling = (
  { ref, token }: ProgressAltScrollTarget,
  remembered: RefObject<ProgressAltScrollPosition>,
) => {
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return undefined;

    element.scrollLeft = remembered.current.scrollLeft;
    element.scrollTop = remembered.current.scrollTop;
    // Read on the way out, while the node is still on the page and holds where it was left.
    return () => {
      remembered.current = { scrollLeft: element.scrollLeft, scrollTop: element.scrollTop };
    };
  }, [ref, remembered, token]);
};

/**
 * Brings the addressed cell into view whenever the address changes — which is how the grid follows the
 * route's selected scope, and how it picks up where the fullscreen page left off. A layout effect, so
 * any movement happens before the arriving grid's first paint rather than as a visible scroll after.
 *
 * Declare this after `useRememberedScrolling`: on a new node that one puts the reader's position back,
 * and this one measures from it. Layout effects run in the order they are written.
 */
export const useRevealedCellScrolling = (
  { ref, token }: ProgressAltScrollTarget,
  target: ProgressAltRevealTarget | undefined,
  layout: ProgressAltCellLayout,
) => {
  const layoutRef = useRef(layout);
  layoutRef.current = layout;

  // Taken apart into coordinates before the effect sees it. Which cell is what should re-run the move;
  // a fresh object carrying the same numbers should not, or the grid would fight the reader's own
  // scrolling on every render. The layout rides in a ref for the same reason.
  const rowIndex = target?.rowIndex;
  const columnIndex = target?.columnIndex;

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element || columnIndex == null) return;

    const { scrollLeft, scrollTop, smooth } = getRevealScroll(
      { rowIndex, columnIndex },
      layoutRef.current,
      element,
    );
    if (scrollLeft === element.scrollLeft && scrollTop === element.scrollTop) return;
    // `scrollTo` is the only way to ask for the animation, and the only thing here jsdom lacks.
    if (smooth && typeof element.scrollTo === 'function') {
      element.scrollTo({ left: scrollLeft, top: scrollTop, behavior: 'smooth' });
      return;
    }
    element.scrollLeft = scrollLeft;
    element.scrollTop = scrollTop;
  }, [ref, token, rowIndex, columnIndex]);
};

/** Where the view was, expressed in terms that survive the columns changing width underneath it. */
export type ColumnScrollAnchor = {
  /** The column whose left edge the view is holding on to. */
  columnIndex: number;
  /** Where that edge sat in the scrollport, so it can be put back exactly there. */
  viewportOffsetPx: number;
  /** Parked against the right end, which is worth keeping as an end rather than as a position. */
  atEnd: boolean;
};

/** Sub-pixel scroll positions are ordinary, so edges are compared with a pixel of slack. */
const EDGE_TOLERANCE_PX = 1;

export type ScrollportMetrics = { scrollLeft: number; scrollWidth: number; clientWidth: number };

/**
 * The first column clear of the date column — the leftmost one the reader can see whole. Scrolled
 * past every column's left edge there is nothing ahead to hold on to, so the last column it is.
 */
const getAnchoredColumnIndex = (columnOffsetsPx: readonly number[], contentLeftPx: number) => {
  const index = columnOffsetsPx.findIndex(offset => offset >= contentLeftPx - EDGE_TOLERANCE_PX);
  return index === -1 ? Math.max(0, columnOffsetsPx.length - 1) : index;
};

/** Reads what the view is looking at, given where each column's left edge currently sits. */
export const getColumnScrollAnchor = (
  { scrollLeft, scrollWidth, clientWidth }: ScrollportMetrics,
  { columnOffsetsPx, stickyInsetPx }: ProgressAltScrollLayout,
): ColumnScrollAnchor => {
  // The date column is sticky against the left edge, so it covers that much of every scroll position.
  const columnIndex = getAnchoredColumnIndex(columnOffsetsPx, scrollLeft + stickyInsetPx);
  return {
    columnIndex,
    viewportOffsetPx: (columnOffsetsPx[columnIndex] ?? 0) - scrollLeft,
    atEnd: scrollLeft >= scrollWidth - clientWidth - EDGE_TOLERANCE_PX,
  };
};

/**
 * Where to scroll to put an anchor back, given where the columns have moved to.
 *
 * A view parked against the right end is held there instead of at any one column: widening the
 * columns pushes content past the right edge, and a reader at the end of the timeline means to be
 * at the end of it. The left end needs no such care — pegging column zero already keeps it.
 */
export const getAnchoredScrollLeft = (
  anchor: ColumnScrollAnchor,
  { columnOffsetsPx }: ProgressAltScrollLayout,
  maxScrollLeft: number,
): number => {
  const target = anchor.atEnd
    ? maxScrollLeft
    : (columnOffsetsPx[anchor.columnIndex] ?? 0) - anchor.viewportOffsetPx;
  return Math.min(Math.max(target, 0), Math.max(maxScrollLeft, 0));
};

/**
 * Holds the view still through a change of layout, so the photos under the reader's eye stay under it
 * rather than sliding off while the tiles grow or the date column collapses out from beside them.
 *
 * The anchor is read during render, the last moment the DOM still holds the layout the reader is
 * looking at, and spent in a layout effect once the new widths are in — before the browser paints,
 * so the view never visibly jumps.
 */
export const useResizeAnchoredScrolling = (
  { ref }: ProgressAltScrollTarget,
  layout: ProgressAltScrollLayout,
  /** Changing this is what the view is held through; its value is never read. */
  layoutKey: string,
) => {
  const anchorRef = useRef<ColumnScrollAnchor | undefined>(undefined);
  const previousKeyRef = useRef(layoutKey);
  const previousLayoutRef = useRef(layout);

  if (previousKeyRef.current !== layoutKey) {
    const element = ref.current;
    anchorRef.current = element ? getColumnScrollAnchor(element, previousLayoutRef.current) : undefined;
    previousKeyRef.current = layoutKey;
  }
  previousLayoutRef.current = layout;

  useLayoutEffect(() => {
    const element = ref.current;
    const anchor = anchorRef.current;
    anchorRef.current = undefined;
    if (!element || !anchor) return;

    element.scrollLeft = getAnchoredScrollLeft(
      anchor,
      layout,
      Math.max(0, element.scrollWidth - element.clientWidth),
    );
  });
};
